import * as React from 'react'
import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Clock, User, Image as ImageIcon, FileText, Mic } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { prefetchWAMessages } from '@/hooks/useWhatsApp'
import type { WAConversation } from '@/lib/api-client'

interface ConversationItemProps {
  conversation: WAConversation
  isSelected: boolean
  onClick: () => void
}

export function ConversationItem({
  conversation,
  isSelected,
  onClick,
}: ConversationItemProps) {
  const queryClient = useQueryClient()
  const { contact, patient, window, unreadCount, lastMessageAt, lastMessagePreview } = conversation

  // Prefetch messages on hover so they're cached before click
  const handleMouseEnter = React.useCallback(() => {
    prefetchWAMessages(queryClient, conversation.id)
  }, [queryClient, conversation.id])

  const displayName = patient?.name || contact.displayName || formatPhone(contact.waPhoneNumber)
  const initials = getInitials(displayName)
  const timeAgo = lastMessageAt ? formatTimeAgo(new Date(lastMessageAt)) : ''
  const preview = getMessagePreview(lastMessagePreview)
  const hasUnread = unreadCount > 0

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      className={cn(
        'w-full flex items-center gap-3 px-3 min-h-[72px] text-left transition-colors duration-150 overflow-hidden',
        isSelected
          ? 'bg-[#f0f2f5]'
          : 'hover:bg-[#f5f6f6]'
      )}
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <Avatar className="w-12 h-12">
          <AvatarImage src={contact.profilePictureUrl || undefined} alt={displayName} />
          <AvatarFallback className="bg-[#dfe5e7] text-[#54656f] text-sm font-semibold">
            {initials}
          </AvatarFallback>
        </Avatar>
        {window.isOpen && (
          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#00a884] border-2 border-white" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 overflow-hidden py-3 border-b border-[#e9edef]">
        {/* Top row: Name + Time */}
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <span className={cn(
            'text-[14px] truncate',
            hasUnread ? 'font-semibold text-[#111b21]' : 'font-normal text-[#111b21]'
          )}>
            {displayName}
          </span>
          <span className={cn(
            'text-[12px] flex-shrink-0',
            hasUnread ? 'text-[#00a884] font-medium' : 'text-[#667781]'
          )}>
            {timeAgo}
          </span>
        </div>

        {/* Patient info */}
        {patient && (
          <div className="flex items-center gap-1 mb-0.5">
            <User className="w-3 h-3 text-[#667781]" />
            <span className="text-[11px] text-[#667781] truncate">
              {patient.pno}
            </span>
            {conversation.branch && (
              <Badge variant="secondary" className="px-1.5 py-0 text-[9px] h-3.5 font-medium bg-[#e9edef] text-[#667781] border-0">
                {conversation.branch.shortName || conversation.branch.name}
              </Badge>
            )}
          </div>
        )}

        {/* Bottom row: Preview + Badges */}
        <div className="flex items-center justify-between gap-2 min-w-0">
          <p className={cn(
            'text-[13px] truncate min-w-0 flex-1',
            hasUnread ? 'text-[#111b21] font-medium' : 'text-[#667781]'
          )}>
            {preview.icon && <span className="inline-flex align-text-bottom mr-1">{preview.icon}</span>}
            {preview.text}
          </p>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {window.isOpen && window.formatted && (
              <span className="hidden sm:flex items-center gap-0.5 text-[10px] text-[#00a884] font-medium">
                <Clock className="w-2.5 h-2.5" />
                {window.formatted}
              </span>
            )}
            {hasUnread && (
              <span className="flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded-full bg-[#25d366] text-white text-[11px] font-bold">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  )
}

// Helper functions

function formatPhone(phone: string): string {
  if (phone.startsWith('60') && phone.length >= 11) {
    const country = phone.slice(0, 2)
    const prefix = phone.slice(2, 4)
    const mid = phone.slice(4, 7)
    const last = phone.slice(7)
    return `+${country} ${prefix}-${mid} ${last}`
  }
  return phone
}

function getInitials(name: string): string {
  const parts = name.split(' ').filter(Boolean)
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return ((parts[0][0] || '') + (parts[1][0] || '')).toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

function formatTimeAgo(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Now'
  if (diffMins < 60) return `${diffMins}m`
  if (diffHours < 24) return `${diffHours}h`
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d`

  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
  })
}

function getMessagePreview(preview: string | null): { text: string; icon?: React.ReactNode } {
  if (!preview) return { text: 'No message' }

  if (preview.startsWith('[Imej]') || preview.startsWith('[Image]')) {
    return { text: 'Photo', icon: <ImageIcon className="w-3 h-3" /> }
  }
  if (preview.startsWith('[Dokumen:') || preview.startsWith('[Document:')) {
    return {
      text: preview.replace('[Dokumen:', '').replace('[Document:', '').replace(']', '').trim() || 'Document',
      icon: <FileText className="w-3 h-3" />,
    }
  }
  if (preview.startsWith('[Audio]')) {
    return { text: 'Audio', icon: <Mic className="w-3 h-3" /> }
  }
  if (preview.startsWith('[Template:')) {
    return { text: preview.replace('[Template:', '').replace(']', '').trim() }
  }

  return { text: preview }
}
